import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';

import React, { useMemo } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { MDash } from 'ui-components/components/mdash';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { GuidService } from 'ui-components/services/guid-service';

import { Disabled, Inactive } from './constants/inventory-sync-status';
import InventoryModalOpener from './inventory-modal-opener';
import { InventorySync } from '../../../../constants/features';
import { FeatureFlagDeclarations } from '../../../../feature-flags/feature-flag-declarations';
import { useFeatureFlag } from '../../../../feature-flags/hooks/use-feature-flag';
import { useIsSellerFeatureEnabled } from '../../../../hooks/use-is-seller-feature-enabled';

import '../../../../prop-types/product-grid/product';

import messages from '../../messages';
import style from './style.module.less';

const nativeInventoryConnectionType = 'shipengine';
const nativeInventoryConnectionName = 'ShipStation Inventory';

/**
 * @param {ProductRowData} productData
 * @returns {ProductRowData}
 */
export const patchInventoryData = (productData) => {
  const { inventory, aggregateInventoryCounts } = productData;
  const { available = 0, stock = 0 } = inventory ?? emptyObject;
  const { committedQuantity: commitedQuantityProp, connectionCounts = emptyArray } = aggregateInventoryCounts ?? emptyObject;
  const committedQuantity = commitedQuantityProp ?? 0;

  if (connectionCounts !== emptyArray) {
    return productData;
  }

  /** @type {ConnectionCount} */
  const patchedNativeConnectionCount = {
    connectionType: nativeInventoryConnectionType,
    connectionName: nativeInventoryConnectionName,
    onhandQuantity: stock,
    availableQuantity: available,
  };

  /** @type {Array<ConnectionCount>} */
  const patchedConnectionCounts = [
    {
      ...patchedNativeConnectionCount,
      connectionId: GuidService.newGuid(),
      isConnectionIncluded: true,
    },
  ];

  /** @type {AggregateInventoryCounts} */
  const newAggregateInventoryCounts = {
    availableQuantity: available,
    committedQuantity,
    excludedQuantity: 0,
    onhandQuantity: stock,
    connectionCounts: patchedConnectionCounts,
  };
  return {
    ...productData,
    aggregateInventoryCounts: newAggregateInventoryCounts,
  };
};

/**
 * @param {ProductRowData} productData
 * @returns {ProductRowData}
 */
export const usePatchedInventoryData = (productData) => {
  return useMemo(() => {
    const { inventory = emptyObject, aggregateInventoryCounts } = productData;
    if (inventory === emptyObject || inventory === null) {
      return {
        ...productData,
        inventory: inventory ?? emptyObject,
        aggregateInventoryCounts: aggregateInventoryCounts ?? emptyObject,
      };
    }
    return patchInventoryData(productData);
  }, [productData]);
};

const AggregateInventoryHeader = () => {
  const hasInventorySync = useIsSellerFeatureEnabled(InventorySync);
  const tooltipMessage = hasInventorySync ? messages.aggregateLastUpdatedAdditionalWithSync : messages.aggregateLastUpdatedAdditional;
  return (
    <Tooltip position='bottom'>
      <TooltipContent>
        <div className={style['tooltip-text']}>
          <p>
            <FormattedMessage {...tooltipMessage} />
          </p>
        </div>
      </TooltipContent>
      <div className={style.header}>
        <FormattedMessage {...messages.totalAvailableInventoryColumnHeader} />
        <FontAwesomeIcon className={style.icon} icon={faInfoCircle} />
      </div>
    </Tooltip>
  );
};

/**
 * @param {ProductRowData} props
 * @returns {JSX.Element}
 */
const AggregateInventoryCell = (props) => {
  const { aggregateInventoryCounts, name, isBundle = false, inventoryDisabled } = usePatchedInventoryData(props);

  const enableConsolidatedBundleData = useFeatureFlag(FeatureFlagDeclarations.enableConsolidatedBundleData);
  const hideForBundles = isBundle && !enableConsolidatedBundleData;

  const hasNoInventoryData = aggregateInventoryCounts === emptyObject;

  if (inventoryDisabled || hasNoInventoryData || hideForBundles) {
    return (
      <div className={classnames(style['column-contents'], style['empty-total-available'])}>
        <MDash />
      </div>
    );
  }

  const { availableQuantity, excludedQuantity = 0, hasValidInventory = true, connectionCounts = emptyArray } = aggregateInventoryCounts;
  const hasFaultyConnection = connectionCounts.some(
    (cnt) => cnt.isConnectionIncluded && (cnt.connectionStatus === Inactive || cnt.connectionStatus === Disabled),
  );
  const cellText = hasValidInventory ? availableQuantity - excludedQuantity : '0';

  return (
    <div className={style['column-contents']}>
      <InventoryModalOpener aggregateInventoryCounts={aggregateInventoryCounts} hasFaultyConnection={hasFaultyConnection} name={name}>
        {cellText}
      </InventoryModalOpener>
      {hasFaultyConnection ? <FontAwesomeIcon className={style['inactive-connection-icon']} icon={faExclamationCircle} /> : null}
    </div>
  );
};

export default {
  getMessageDescriptor: () => messages.totalAvailableInventoryColumnHeader,
  name: <AggregateInventoryHeader />,
  render: AggregateInventoryCell,
};
